import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { Card, TextField, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import CustomizedDialogs from '../../shared/components/CustomizedDialogs';
import Logo from '../../shared/components/Logo';
import NavTabs from '../../shared/components/NavTabs';
import SnackbarAlert from '../../shared/components/SnackbarAlert';
import SpinnerOverlay from '../../shared/components/SpinnerOverlay/SpinnerOverlay';
import SnackConfig from '../../shared/config/snackConfig';
import { loginUser, signUpNewUser, verifyToken } from '../../shared/services/user';

const useStyles = makeStyles({
  container: {
    width: '100%',
    height: '90vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '& img': {
      maxWidth: '100%',
      height: '80px',
      margin: '40px',
    },
    '& .mobile-login': {
      width: '350px',
      minHeight: '450px',
      '& form': {
        padding: '20px 30px',
        height: '360px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        '& *': {
          width: '100%',
        },
        '& .input-group': {
          height: '300px',
          display: 'flex',
          flexDirection: 'column',
          '& div': {
            marginTop: '15px',
          },
        },
      },
    },
  },
  tabs: {
    width: '175px !important',
  },
  '.Mui-selected': {
    background: 'rgba(0, 0, 0, 0.9) !important',
  },
});

interface LoginProps {
  basePath: string;
}

const Login: React.FC<LoginProps> = ({ basePath }: LoginProps) => {
  const classes = useStyles();

  const [loading, setLoading] = useState<boolean>(true);
  const [snackConfig, setSnackConfig] = useState<SnackConfig>(new SnackConfig({}));
  const [redirectToDashboard, setRedirectToDashboard] = useState<boolean>(false);
  const [loggingAction, setLoggingAction] = useState<boolean>(false);

  const [customDialogState, setCustomDialogState] = useState<boolean>(false);
  const [form, setForm] = useState<Record<string, Record<string, string>>>({
    loginForm: {
      email: '',
      password: '',
    },
    signUpForm: {
      firstName: '',
      lastName: '',
      email: '',
      companyName: '',
      mobileNumber: '',
    },
  });

  useEffect(() => {
    void (async (): Promise<void> => {
      const valid = await verifyToken();
      if (valid) {
        setRedirectToDashboard(true);
      }
      setLoading(false);
    })();
  }, []);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, formName: string) => {
    const newForm = { ...form };
    const { name: fieldName, value } = event.target;
    newForm[formName][fieldName] = value;
    setForm(newForm);
  };

  const handleLogin = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setLoggingAction(true);
    const { id } = event.target as HTMLFormElement;
    const { email, password } = form[id];

    const result = await loginUser(email, password);
    const { err, context } = result;
    if (err) {
      setSnackConfig(new SnackConfig({ open: true, message: context }));
    } else {
      setRedirectToDashboard(true);
    }
    setLoggingAction(false);
  };

  const handleSignUp = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setLoggingAction(true);
    const { id } = event.target as HTMLFormElement;
    const { email, firstName, lastName, companyName, mobileNumber } = form[id];
    const result = await signUpNewUser(email, firstName, lastName, companyName, mobileNumber);
    const { err, context } = result;
    if (err) {
      setSnackConfig(new SnackConfig({ open: true, message: context }));
    } else {
      setCustomDialogState(true);
    }
    setLoggingAction(false);
  };

  if (redirectToDashboard) {
    return <Redirect to="/" />;
  }

  return (
    <div>
      {!loading && (
        <div>
          {customDialogState ? (
            <CustomizedDialogs
              customDialogState={customDialogState}
              setCustomDialogState={setCustomDialogState}
              message="We have received your request and will process it shortly. We will send you an email once onboarding is done."
            />
          ) : (
            ''
          )}
          <div className={classes.container}>
            <Logo href="https://adeptdc.com" aProps={{ target: '_blank', rel: 'noopener noreferrer' }} />
            <Card className="mobile-login">
              <NavTabs
                tabs={[
                  { label: 'Login', value: '', to: '/', className: classes.tabs },
                  { label: 'Sign Up', value: 'signUp', to: '/signUp', className: classes.tabs },
                ]}
                basePath={basePath}
              >
                <Route exact path="/">
                  <SpinnerOverlay active={loggingAction}>
                    <form id="loginForm" onSubmit={handleLogin}>
                      <div className="input-group">
                        <TextField
                          id="email"
                          InputLabelProps={{ required: false, htmlFor: 'email' }}
                          label="Email"
                          type="email"
                          value={form.loginForm.email}
                          onChange={(e) => handleChange(e, 'loginForm')}
                          name="email"
                          required
                        />
                        <TextField
                          id="password"
                          InputLabelProps={{ required: false, htmlFor: 'password' }}
                          label="Password"
                          type="password"
                          value={form.loginForm.password}
                          onChange={(e) => handleChange(e, 'loginForm')}
                          name="password"
                          required
                        />
                      </div>
                      <Button type="submit" variant="outlined">
                        Login
                      </Button>
                    </form>
                  </SpinnerOverlay>
                </Route>
                <Route exact path="/signUp">
                  <SpinnerOverlay active={loggingAction}>
                    <form id="signUpForm" onSubmit={handleSignUp}>
                      <div className="input-group">
                        {/* 2 hidden inputs to avoid auto fill of username & password */}
                        <input type="hidden" value="something" />
                        <input type="hidden" value="something" />
                        <TextField
                          id="firstName"
                          InputLabelProps={{ required: false, htmlFor: 'firstName' }}
                          label="First Name"
                          type="text"
                          value={form.signUpForm.firstName}
                          onChange={(e) => handleChange(e, 'signUpForm')}
                          name="firstName"
                          required
                        />
                        <TextField
                          id="lastName"
                          InputLabelProps={{ required: false, htmlFor: 'lastName' }}
                          label="Last Name"
                          type="text"
                          value={form.signUpForm.lastName}
                          onChange={(e) => handleChange(e, 'signUpForm')}
                          name="lastName"
                          required
                        />
                        <TextField
                          id="email"
                          InputLabelProps={{ required: false, htmlFor: 'email' }}
                          label="Email"
                          type="email"
                          value={form.signUpForm.email}
                          onChange={(e) => handleChange(e, 'signUpForm')}
                          name="email"
                          required
                        />
                        <TextField
                          id="companyName"
                          InputLabelProps={{ required: false, htmlFor: 'companyName' }}
                          label="Company Name"
                          type="text"
                          value={form.signUpForm.password}
                          onChange={(e) => handleChange(e, 'signUpForm')}
                          name="companyName"
                          required
                        />
                        <TextField
                          id="mobileNumber"
                          InputLabelProps={{ required: false, htmlFor: 'mobileNumber' }}
                          label="Phone Number"
                          type="text"
                          value={form.signUpForm.mobileNumber}
                          onChange={(e) => handleChange(e, 'signUpForm')}
                          name="mobileNumber"
                          required
                        />
                      </div>
                      <Button type="submit" variant="outlined">
                        Sign Up
                      </Button>
                    </form>
                  </SpinnerOverlay>
                </Route>
              </NavTabs>
            </Card>
            <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
